'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Card } from './ui/card';
import { CheckCircle2 } from 'lucide-react';
import { getAllModuleProgress, type ModuleProgress } from '@/lib/storage/moduleProgress';

interface ModuleInfo {
  id: string;
  title: string;
  description: string;
  estimatedDuration: string;
  level: string;
}

interface ModuleListWithProgressProps {
  modules: ModuleInfo[];
}

export function ModuleListWithProgress({ modules }: ModuleListWithProgressProps) {
  const [progressMap, setProgressMap] = useState<Record<string, ModuleProgress>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getAllModuleProgress()
      .then((allProgress) => {
        const map: Record<string, ModuleProgress> = {};
        allProgress.forEach((p) => {
          map[p.moduleId] = p;
        });
        setProgressMap(map);
      })
      .catch((error) => {
        console.error('Failed to load module progress:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-3 sm:space-y-4">
        {modules.map((module) => (
          <Card key={module.id} className="p-4 sm:p-5 animate-pulse">
            <div className="h-6 bg-muted rounded w-3/4 mb-2" />
            <div className="h-4 bg-muted rounded w-full" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {modules.map((module) => {
        const progress = progressMap[module.id];
        const isCompleted = progress?.completed || false;
        const isViewed = progress?.viewed || false;

        return (
          <Link
            key={module.id}
            href={`/modules/${module.id}`}
            className="block transition-transform hover:scale-[1.01] active:scale-[0.99]"
          >
            <Card
              className={`p-3 sm:p-4 md:p-5 hover:shadow-lg transition-all border-l-4 group cursor-pointer ${
                isCompleted
                  ? 'border-l-green-500 bg-green-50/50 dark:bg-green-950/10'
                  : 'border-l-primary'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3 mb-2">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  {isCompleted && (
                    <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                  )}
                  <h2
                    className={`text-base sm:text-lg md:text-xl font-semibold break-words group-hover:text-primary transition-colors flex-1 ${
                      isCompleted ? 'line-through text-muted-foreground' : ''
                    }`}
                  >
                    {module.title}
                  </h2>
                </div>
                <div className="flex flex-row gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground shrink-0">
                  {isCompleted && (
                    <span className="px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs font-medium">
                      Completed
                    </span>
                  )}
                  <span className="uppercase text-xs font-medium px-2 py-0.5 rounded bg-muted whitespace-nowrap">
                    {module.level}
                  </span>
                  <span className="flex items-center whitespace-nowrap">
                    {module.estimatedDuration}
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-6">{module.description}</p>
              <div className="mt-3 text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                {isCompleted ? 'Review module →' : 'Open module →'}
              </div>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}
